import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { makePageSpaceChecker } from '../pdfPageSpace';
import { DashboardMetrics } from '../../../services/dashboardService';

interface DashboardReportGeneratorProps {
  metrics: DashboardMetrics | undefined;
  registrosPorObra: Array<{
    obra: string;
    registros: any[];
    total: number;
    completados: number;
    pendientes: number;
  }>;
  chartData: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
    }>;
  };
  fechaGeneracion: string;
  filtroFecha?: string;
}

const DashboardReportGenerator: React.FC<DashboardReportGeneratorProps> = ({
  metrics,
  registrosPorObra,
  chartData,
  fechaGeneracion,
  filtroFecha = 'Todos los registros'
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generarReportePDF = async () => {
    if (!metrics) return;
    
    setIsGenerating(true);
    
    try {
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      
      // Función auxiliar para verificar espacio y agregar página si es necesario
      const checkPageSpace = makePageSpaceChecker(doc, pageHeight, margin, () => yPosition, (y) => { yPosition = y; });
      
      // Configuración del documento
      doc.setFont('helvetica');
      doc.setFontSize(20);
      
      // Título principal
      doc.text('Reporte de Dashboard - Control de Limpieza', 20, 30);
      
      // Fecha de generación y filtros
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generado el: ${fechaGeneracion}`, 20, 45);
      doc.text(`Período: ${filtroFecha}`, 20, 55);
      
      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 65, 190, 65);
      
      let yPosition = 80;
      
      // Resumen General
      checkPageSpace(40);
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Resumen General', 20, yPosition);
      yPosition += 20;
      
      // Métricas principales en tabla
      const resumenData = [
        ['Total Áreas', metrics.totalAreas.toString()],
        ['Tareas Activas', metrics.tareasActivas.toString()],
        ['Total Tareas', metrics.totalTareas.toString()],
        ['Registros', metrics.registrosHoy.toString()],
        ['% Completados', `${metrics.porcentajeCompletados}%`],
        ['Áreas Pendientes', metrics.areasPendientes.toString()],
        ['Áreas Completadas', metrics.areasCompletadas.toString()]
      ];
      
      checkPageSpace(resumenData.length * 15 + 30);
      autoTable(doc, {
        startY: yPosition,
        head: [['Métrica', 'Valor']],
        body: resumenData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        styles: { fontSize: 10 },
        margin: { top: 20, bottom: 20 }
      });
      
      yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (resumenData.length * 15 + 50);
      
      // Sección Top Usuarios eliminada
      
      // Registros por Obra
      if (registrosPorObra && registrosPorObra.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Actividades por Obra', 20, yPosition);
        yPosition += 20;
        
        const obraData = registrosPorObra.map(grupo => [
          grupo.obra,
          grupo.total.toString(),
          grupo.completados.toString(),
          grupo.pendientes.toString()
        ]);
        
        checkPageSpace(obraData.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Obra', 'Total', 'Completados', 'Pendientes']],
          body: obraData,
          theme: 'grid',
          headStyles: { fillColor: [245, 158, 11], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (obraData.length * 12 + 50);
      }
      
      // Sección Tendencia de Registros eliminada
      
      // Áreas por Estado
      if (metrics.areasPorEstado && metrics.areasPorEstado.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Distribución de Áreas por Estado', 20, yPosition);
        yPosition += 20;
        
        const areasData = metrics.areasPorEstado.map(area => [
          area.estado,
          area.cantidad.toString(),
          `${area.porcentaje}%`
        ]);
        
        checkPageSpace(areasData.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Estado', 'Cantidad', 'Porcentaje']],
          body: areasData,
          theme: 'grid',
          headStyles: { fillColor: [239, 68, 68], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (areasData.length * 12 + 50);
      }
      

      
      // Pie de página
      checkPageSpace(20);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Sistema de Control de Limpieza - ControlLimpio', 20, yPosition);
      yPosition += 5;
      doc.text('Reporte generado automáticamente', 20, yPosition);
      
      // Guardar el PDF
      const nombreArchivo = `Dashboard_Reporte_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nombreArchivo);
      
    } catch (error) {
      console.error('Error al generar el reporte PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Generador de Reportes</h3>
          <p className="text-sm text-gray-600">
            Genera reportes en PDF del dashboard con todas las métricas y tendencias
          </p>
        </div>
        <button
          onClick={generarReportePDF}
          disabled={isGenerating || !metrics}
          className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            isGenerating || !metrics
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          }`}
        >
          {isGenerating ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generando...
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generar Reporte PDF
            </div>
          )}
        </button>
      </div>
      
      {!metrics && (
        <div className="text-center py-4">
          <div className="text-gray-500 text-sm">
            No hay datos disponibles para generar el reporte
          </div>
        </div>
      )}
      
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
          <div className="bg-blue-50 p-3 rounded-md">
            <div className="font-medium text-blue-900">Métricas Principales</div>
            <div className="text-blue-700">✓ Incluidas en el reporte</div>
          </div>
          {/* Top Usuarios y Tendencias eliminados del reporte */}
          <div className="bg-orange-50 p-3 rounded-md">
            <div className="font-medium text-orange-900">Actividades por Obra</div>
            <div className="text-orange-700">✓ Incluidas en el reporte</div>
          </div>

          <div className="bg-indigo-50 p-3 rounded-md">
            <div className="font-medium text-indigo-900">Distribución de Áreas</div>
            <div className="text-indigo-700">✓ Incluida en el reporte</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardReportGenerator;
